import { useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { format, isSameDay } from 'date-fns'
import type { GuestSpot, Artist } from '@/types/models'
import { useTheme } from '@/contexts/ThemeContext'

interface GuestArtistsMapProps {
  guestSpots: GuestSpot[]
  artistsMap: Map<string, Artist>
  selectedCity: string
  selectedCityCoordinates: { latitude: number; longitude: number } | null
}

interface TripLine {
  id: string
  start: [number, number]
  end: [number, number]
  artistName: string
  dates: string
}

const CITY_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  Helsinki: { latitude: 60.1699, longitude: 24.9384 },
  Tampere: { latitude: 61.4978, longitude: 23.761 },
  Tallinn: { latitude: 59.437, longitude: 24.7536 },
  Amsterdam: { latitude: 52.3676, longitude: 4.9041 },
}

const MAP_BOUNDS_PADDING_RATIO = 0.12
const SINGLE_CITY_FALLBACK_ZOOM = 5
const MAX_MAP_ZOOM = 8

function formatGuestSpotDateRange(startDate: Date, endDate: Date): string {
  if (isSameDay(startDate, endDate)) {
    return format(startDate, 'MMM d')
  }

  return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
}

function MapBoundsHandler({ mapBoundsPoints }: { mapBoundsPoints: [number, number][] }) {
  const map = useMap()

  useEffect(() => {
    if (mapBoundsPoints.length === 0) return

    if (mapBoundsPoints.length === 1) {
      map.setView(mapBoundsPoints[0], SINGLE_CITY_FALLBACK_ZOOM, { animate: false })
      return
    }

    const bounds = L.latLngBounds(mapBoundsPoints).pad(MAP_BOUNDS_PADDING_RATIO)

    map.fitBounds(bounds, {
      padding: [12, 12],
      animate: false,
      maxZoom: MAX_MAP_ZOOM,
    })
  }, [mapBoundsPoints, map])

  return null
}

const createDestinationIcon = () => {
  const size = 12
  return L.divIcon({
    html: `
      <div class="guest-destination-marker">
        <div class="guest-destination-dot"></div>
      </div>
    `,
    className: 'guest-destination-marker-wrapper',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

export function GuestArtistsMap({ guestSpots, artistsMap, selectedCity, selectedCityCoordinates }: GuestArtistsMapProps) {
  const { currentThemeMode } = useTheme()
  const destinationIcon = useMemo(() => createDestinationIcon(), [])

  const destination = useMemo(() => {
    if (selectedCityCoordinates) {
      return [selectedCityCoordinates.latitude, selectedCityCoordinates.longitude] as [number, number]
    }

    const firstSpot = guestSpots[0]
    if (firstSpot?.targetLat && firstSpot?.targetLon) {
      return [firstSpot.targetLat, firstSpot.targetLon] as [number, number]
    }

    const fallback = CITY_COORDINATES[selectedCity] || CITY_COORDINATES.Helsinki
    return [fallback.latitude, fallback.longitude] as [number, number]
  }, [guestSpots, selectedCity, selectedCityCoordinates])

  const tripLines = useMemo(() => {
    const lines: TripLine[] = []

    guestSpots.forEach((spot, index) => {
      const isAutomatedSpot = !!spot.username
      const isManualSpot = !!spot.artistId

      if (isManualSpot) {
        const artist = artistsMap.get(spot.artistId!)
        if (!artist?.location.lat || !artist?.location.lon) return

        lines.push({
          id: `manual-${spot.id}-${index}`,
          start: [artist.location.lat, artist.location.lon],
          end: destination,
          artistName: artist.displayName || 'Guest artist',
          dates: formatGuestSpotDateRange(spot.startDate, spot.endDate),
        })
        return
      }

      if (isAutomatedSpot) {
        if (!spot.homeLat || !spot.homeLon) return

        lines.push({
          id: `auto-${spot.id}-${index}`,
          start: [spot.homeLat, spot.homeLon],
          end: destination,
          artistName: spot.username ? `@${spot.username}` : 'Guest artist',
          dates: formatGuestSpotDateRange(spot.startDate, spot.endDate),
        })
      }
    })

    return lines
  }, [artistsMap, destination, guestSpots])

  const mapBoundsPoints = useMemo(() => {
    const points: [number, number][] = [destination]

    tripLines.forEach((tripLine) => {
      points.push(tripLine.start)
    })

    return points
  }, [destination, tripLines])

  const isDesktop = typeof window !== 'undefined' ? window.innerWidth >= 768 : true

  return (
    <>
      <style>{`
        .guest-destination-marker-wrapper {
          background: none !important;
          border: none !important;
        }

        .guest-destination-marker {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
        }

        .guest-destination-dot {
          width: 10px;
          height: 10px;
          background: #ef4444;
          border: 2px solid ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
          border-radius: 50%;
          box-shadow: 0 2px 6px ${currentThemeMode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'};
        }

        .leaflet-popup-content-wrapper {
          border-radius: 8px;
          background-color: ${currentThemeMode === 'dark' ? 'rgba(42, 42, 42, 0.95)' : 'white'} !important;
        }

        .leaflet-popup-content {
          margin: 10px 12px;
          min-width: 180px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'} !important;
        }

        .guest-artists-map .leaflet-container {
          background: hsl(var(--background));
          border-radius: 0.5rem;
        }

        .guest-artists-map .leaflet-control-attribution {
          display: none !important;
        }

        .guest-popup-name {
          font-weight: 600;
          font-size: 14px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'};
        }

        .guest-popup-dates {
          font-size: 12px;
          color: ${currentThemeMode === 'dark' ? '#a0a0a0' : '#6b7280'};
          margin-top: 4px;
        }

        /* Apply grayscale only to map tiles, not markers */
        .guest-artists-map .leaflet-tile {
          filter: ${currentThemeMode === 'dark' ? 'grayscale(100%) contrast(1.1) brightness(0.65)' : 'grayscale(100%) contrast(1.1)'};
        }

        /* Make text labels and controls light in dark mode */
        ${currentThemeMode === 'dark' ? `
          .guest-artists-map .leaflet-control-attribution,
          .guest-artists-map .leaflet-tooltip {
            background-color: rgba(42, 42, 42, 0.95) !important;
            color: #ffffff !important;
          }

          .guest-artists-map .leaflet-popup-tip {
            background-color: rgba(42, 42, 42, 0.95) !important;
          }

          .guest-artists-map .leaflet-control-zoom a {
            background-color: #2a2a2a !important;
            color: #ffffff !important;
            border-color: #3a3a3a !important;
          }

          .guest-artists-map .leaflet-control-zoom a:hover {
            background-color: #333333 !important;
          }
        ` : ''}
      `}</style>

      <div className="w-full h-full bg-background guest-artists-map">
        <MapContainer
          center={destination}
          zoom={5}
          style={{ height: '100%', width: '100%' }}
          className="z-10"
          zoomControl={isDesktop}
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />

          <MapBoundsHandler mapBoundsPoints={mapBoundsPoints} />

          {tripLines.map(line => (
            <Polyline
              key={line.id}
              positions={[line.start, line.end]}
              color="#ef4444"
              weight={2}
              opacity={0.65}
            />
          ))}

          <Marker position={destination} icon={destinationIcon} zIndexOffset={1000}>
            <Popup>
              <div className="guest-popup-name">{selectedCity || 'Selected city'}</div>
              <div className="guest-popup-dates">Destination</div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>
    </>
  )
}
